import React from 'react';
import classNames from 'classnames';
import { Heart, ThumbUp as ThumbUpFill } from '@wix/wix-ui-icons-common/on-stage/reactions';
import { LikeHeart, ThumbUp, ChevronDownSmall } from '@wix/wix-ui-icons-common/on-stage';
import { useTPAComponentsConfig } from '../TPAComponentsProvider';
import styles from './Reactions.module.scss';
/*
 * WUT's own `defaultIcons` (Reactions.js), same imports. The DEFAULT reaction never uses the
 * consumer's `reactionIcon`: the popover shows WUT's FILLED icon for it and the trigger shows
 * the EMPTY one until something is selected. That is why master's emotions footer is an outline
 * heart while the first popover item is a filled one.
 */
const defaultIcons = {
    heart: { fill: React.createElement(Heart, null), empty: React.createElement(LikeHeart, null) },
    thumbsup: { fill: React.createElement(ThumbUpFill, null), empty: React.createElement(ThumbUp, null) },
};
const LONG_PRESS_MS = 400;
/**
 * WUT's internal data-hooks, copied verbatim from
 * `wix-ui-tpa/dist/cjs/components/Reactions/dataHooks.js`. The Sled drivers query these
 * directly, so they are part of the contract — not an implementation detail.
 */
export const ReactionsDataHooks = {
    PopoverContent: 'reactions-popover-content',
    TriggerButton: 'reactions-trigger-button',
    SubTriggerButton: 'reactions-subtrigger-button',
    ReactionLabel: 'reaction-label',
    HasReaction: 'has-reaction',
};
/**
 * Drop-in replacement for `Reactions` from `wix-ui-tpa/cssVars`.
 *
 * The DOM mirrors WUT's composition (Reactions.js -> ReactionButton.js / ReactionList.js):
 *
 *   root (Popover root, theme state)
 *     reactionButtonRoot (ReactionButton root; active / defaultReaction / animate states)
 *       button.reactionButton          data-hook="reactions-trigger-button [has-reaction]"
 *         span.prefix                  the 24px box TextButton renders as its prefix
 *         span                         data-hook="reaction-label" — VISIBLE, as in WUT
 *       button.popoverTrigger          data-hook="reactions-subtrigger-button", desktop only,
 *                                      screen-reader-only unless :visibleTrigger
 *     div.popover > div.popoverContent data-hook="reactions-popover-content"
 *       div.listRoot[role=menu]        ReactionList root
 *         div.item[role=menuitem][data-id]
 *           div.iconWrapper            24x24
 *           div.itemLabel              hidden; shown on hover as a pill above the row
 *
 * `data-part` attributes are kept alongside so consumer stylesheets can reach the internals
 * without importing this file — that is what replaces WUT's `::popoverElement` /
 * `::popoverContent` pseudo-elements (see emotions-button.scss).
 *
 * Interaction model (WUT drives this with Popper plus hover-intent timers; this is the simpler
 * equivalent that was already in place):
 *  - click trigger        -> select `defaultReaction`, or deselect if already selected
 *  - hover / focus        -> open the popover (desktop)
 *  - long-press           -> open the popover (touch)
 *  - ArrowUp / ArrowDown  -> open the popover from the keyboard
 *  - Escape / outside     -> close
 */
export function Reactions({ className, defaultReaction, additionalReactions, selectedId, reactionLabel, reactionIcon, onReactionSelect, popoverTriggerAriaLabel, theme = 'light', mobile: mobileProp, disabled = false, 'data-hook': dataHook, }) {
    /*
     * `mobile` comes from TPAComponentsContext when the caller does not pass it: `@wix/
     * yoshi-flow-editor` wraps every widget in WUT's own `TPAComponentsProvider`, so every WUT
     * component in this app received it for free, while these ports took a prop nothing passed and
     * stayed permanently on their desktop branch. A prop still wins.
     */
    const tpaConfig = useTPAComponentsConfig();
    const mobile = mobileProp ?? tpaConfig.mobile ?? false;
    const rtl = tpaConfig.rtl ?? false;
    const [isOpen, setIsOpen] = React.useState(false);
    const containerRef = React.useRef(null);
    const longPressTimer = React.useRef();
    /*
     * `animate` and `isTriggerVisible` are `ReactionButton`'s own states, and this port emitted
     * neither — so ~15KB of its sheet was unmatched, including two behaviours users can see:
     *
     *   .reactionButtonRoot.active.animate … .prefix svg   the 0.3s pop when a reaction is applied
     *   .reactionButtonRoot:not(.visibleTrigger) …         which SR-only-clips the sub-trigger
     *
     * The second one matters most: without the state the class is always absent, so the
     * "more reactions" button stayed permanently clipped to 1px and a keyboard user could never see
     * it. Upstream reveals it when focus arrives FROM THE KEYBOARD — hence `isPressedKey`, a flag a
     * document-level keydown listener holds for exactly one frame.
     */
    /*
     * `activeIdx` + `itemRefs`, as `ReactionList.js` keeps them: the list is a roving-tabindex menu.
     * Its imperative `focus()` sets the index to 0, an effect focuses whatever element that index
     * maps to, and the item handler moves it with ArrowLeft/ArrowRight (the list is horizontal),
     * Home and End. Without any of it the items stayed `tabIndex={-1}` and unfocused, so their
     * Enter/Space handlers were unreachable and a keyboard user could open the popover but never
     * choose a reaction from it.
     */
    const [activeIdx, setActiveIdx] = React.useState(undefined);
    /*
     * `ReactionButton`'s imperative handle is `focus: () => subTriggerRef.current?.focus()`, and
     * `Reactions.js` calls it on Escape and after a selection made while the popover was open. Without
     * it the focused item unmounts with the popover and focus falls to <body>, which loses a keyboard
     * user their place entirely. Undefined on mobile, where the sub-trigger is not rendered — the same
     * no-op upstream's optional chaining gives.
     */
    const subTriggerRef = React.useRef(null);
    const itemRefs = React.useRef(new Map());
    const [animate, setAnimate] = React.useState(false);
    const [isTriggerVisible, setTriggerVisibility] = React.useState(false);
    const isPressedKey = React.useRef(false);
    const reactionButtonRootRef = React.useRef(null);
    const animationFrame = React.useRef();
    const open = () => !disabled && setIsOpen(true);
    const close = () => setIsOpen(false);
    // Close on outside pointer and on Escape.
    React.useEffect(() => {
        if (!isOpen) {
            return;
        }
        const onPointerDown = (e) => {
            if (!containerRef.current?.contains(e.target)) {
                close();
            }
        };
        const onKeyDown = (e) => {
            if (e.key === 'Escape') {
                /* `onPopoverKeyDown`'s Escape branch: close, then put focus back on the trigger. */
                close();
                subTriggerRef.current?.focus();
            }
        };
        document.addEventListener('pointerdown', onPointerDown);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('pointerdown', onPointerDown);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [isOpen]);
    React.useEffect(() => () => clearTimeout(longPressTimer.current), []);
    React.useEffect(() => {
        if (activeIdx !== undefined) {
            itemRefs.current.get(activeIdx)?.focus();
        }
    }, [activeIdx]);
    /* Closing the popover releases the roving index, so the next keyboard open starts at the first
       item again rather than wherever the pointer last hovered. */
    React.useEffect(() => {
        if (!isOpen) {
            setActiveIdx(undefined);
        }
    }, [isOpen]);
    /* `useEffect(… , [active, selectedReaction.id])` in ReactionButton.js: applying a reaction — or
       switching which one is applied while still active — replays the pop. */
    React.useEffect(() => {
        if (selectedId) {
            setAnimate(true);
        }
    }, [selectedId]);
    /* The keyboard-vs-pointer discriminator: `isPressedKey` is true only for the frame after a
       keydown, so focus that follows a click does not reveal the sub-trigger. */
    React.useEffect(() => {
        let frame;
        const onDocumentKeyDown = () => {
            isPressedKey.current = true;
            frame = requestAnimationFrame(() => {
                isPressedKey.current = false;
            });
        };
        document.addEventListener('keydown', onDocumentKeyDown);
        return () => {
            cancelAnimationFrame(frame);
            cancelAnimationFrame(animationFrame.current);
            document.removeEventListener('keydown', onDocumentKeyDown);
        };
    }, []);
    const onTriggerFocus = () => {
        if (isPressedKey.current) {
            cancelAnimationFrame(animationFrame.current);
            setTriggerVisibility(true);
        }
    };
    const onTriggerBlur = () => {
        if (isPressedKey.current && !reactionButtonRootRef.current?.contains(document.activeElement)) {
            /* rAF so the check runs after focus has settled on its new target; upstream does the same,
               and skips hiding while the popover is open. */
            animationFrame.current = requestAnimationFrame(() => {
                if (!isOpen) {
                    setTriggerVisibility(false);
                }
            });
        }
    };
    /*
     * `reactionDefinitions` and `selectedReaction`, ported from Reactions.js:
     *
     *   reactionDefinitions = [defaultReaction (WUT's FILLED icon), ...additionalReactions]
     *   selectedReaction    = selectedId ? that definition : defaultReaction with the EMPTY icon
     *
     * Both halves matter: the popover lists the default reaction as well as `additionalReactions`,
     * and an unselected trigger shows the OUTLINE icon, not the consumer's filled one.
     */
    const iconsFor = defaultIcons[defaultReaction];
    const reactionDefinitions = React.useMemo(() => [
        {
            id: defaultReaction,
            label: reactionLabel(defaultReaction),
            icon: iconsFor ? iconsFor.fill : reactionIcon(defaultReaction),
        },
        ...additionalReactions.map((id) => ({
            id,
            label: reactionLabel(id),
            icon: reactionIcon(id),
        })),
    ], 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [defaultReaction, additionalReactions, reactionLabel, reactionIcon]);
    const selectedReaction = selectedId
        ? reactionDefinitions.find((item) => item.id === selectedId)
        : {
            id: defaultReaction,
            label: reactionLabel(defaultReaction),
            icon: iconsFor ? iconsFor.empty : reactionIcon(defaultReaction),
        };
    /*
     * `doesMainButtonTriggerPopover` in ReactionButton.js. On MOBILE, with nothing selected yet, the
     * main button OPENS THE MENU — it does not toggle the default reaction. Always toggling instead
     * means the menu never opens on mobile, so nothing can select a reaction from it.
     */
    const doesMainButtonTriggerPopover = mobile && !selectedId;
    /* `TIMER_DELAY = 500` in ReactionButton.js: hovering arms a timer rather than opening at once. */
    const hoverTimer = React.useRef();
    const openAfterDelay = () => {
        clearTimeout(hoverTimer.current);
        hoverTimer.current = setTimeout(open, 500);
    };
    const closeAndCancel = () => {
        clearTimeout(hoverTimer.current);
        close();
    };
    React.useEffect(() => () => clearTimeout(hoverTimer.current), []);
    const handleTriggerClick = (e) => {
        if (disabled) {
            return;
        }
        if (doesMainButtonTriggerPopover) {
            open();
            return;
        }
        /* Upstream prevents the button taking focus on this branch. */
        e.preventDefault();
        onReactionSelect(selectedId ? null : defaultReaction);
    };
    /* `toggleSelection` in Reactions.js: closing returns focus to the sub-trigger. */
    const closeAndRestoreFocus = () => {
        close();
        subTriggerRef.current?.focus();
    };
    const handleSelect = (reaction) => {
        closeAndRestoreFocus();
        if (!disabled) {
            onReactionSelect(reaction);
        }
    };
    return (React.createElement("div", { ref: containerRef, className: classNames(styles.root, 
        /*
         * The reference app renders this Popover WIRED (the state is on the root in prod's DOM),
         * which is what turns on `.wired .popoverContent`: a 1px border, the themed background and
         * `--wix-ui-tpa-popover-border-radius`. Without it the white box loses its border and comes
         * out 2px smaller than master's (248x52 against 250x54).
         */
        styles.wired, styles[`theme-${theme}`], { [styles.disabled]: disabled }, className), "data-hook": dataHook, "data-mobile": mobile, 
        /* Hover only drives the menu on DESKTOP: upstream passes `onMouseEnter` / `onMouseLeave` to
           the trigger as `!mobile ? … : undefined`, and on mobile it is the click that opens. */
        onMouseEnter: mobile ? undefined : openAfterDelay, onMouseLeave: mobile ? undefined : closeAndCancel },
        React.createElement("div", { "data-part": "popoverElement", className: styles.popoverElement },
            React.createElement("div", { ref: reactionButtonRootRef, className: classNames(styles.reactionButtonRoot, styles[`defaultReaction-${defaultReaction}`], {
                    [styles.active]: !!selectedId,
                    [styles.animate]: animate,
                    [styles.visibleTrigger]: isTriggerVisible,
                }), 
                /* `onAnimationEnd` clears it, as upstream does — the class must not outlive the
                   animation or a re-render would not replay it. */
                onAnimationEnd: () => setAnimate(false), 
                /* Upstream wires these only on desktop (`!mobile ? onFocus : undefined`); on mobile the
                   sub-trigger is not rendered at all. */
                onFocus: mobile ? undefined : onTriggerFocus, onBlur: mobile ? undefined : onTriggerBlur },
                React.createElement("button", { type: "button", "data-part": "trigger", "data-hook": `${ReactionsDataHooks.TriggerButton}${selectedId ? ` ${ReactionsDataHooks.HasReaction}` : ''}`, 
                    /* WUT passes `priority={TextButtonPriority.secondary}` to this TextButton
                     (ReactionButton.js), which is where the trigger's colour comes from. */
                    /* `TextButton` reads TPAComponentsConsumer and writes `mobile` itself, which is where
                       its 14px/1.44em mobile font column comes from. */
                    className: classNames(styles.reactionButton, styles['priority-secondary'], {
                        [styles.mobile]: mobile,
                    }), ...(doesMainButtonTriggerPopover
                        ? { 'aria-haspopup': 'menu', 'aria-expanded': isOpen }
                        : { 'aria-pressed': !!selectedId }), disabled: disabled, onClick: handleTriggerClick, onFocus: mobile ? undefined : open, onKeyDown: (e) => {
                        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                            e.preventDefault();
                            open();
                            /* `ReactionList`'s imperative `focus()` is `setActiveIdx(0)`; ReactionButton calls
                               it after opening from the keyboard, which is what puts focus in the menu. */
                            setActiveIdx(0);
                        }
                    }, onTouchStart: () => {
                        longPressTimer.current = setTimeout(open, LONG_PRESS_MS);
                    }, onTouchEnd: () => clearTimeout(longPressTimer.current) },
                    React.createElement("span", { "data-part": "icon", className: styles.prefix }, selectedReaction?.icon),
                    React.createElement("span", { className: styles.content },
                        React.createElement("span", { "data-part": "reaction-label", "data-hook": ReactionsDataHooks.ReactionLabel }, selectedReaction?.label))),
                !mobile && (React.createElement("button", { type: "button", ref: subTriggerRef, className: styles.popoverTrigger, "data-hook": ReactionsDataHooks.SubTriggerButton, "aria-label": popoverTriggerAriaLabel, "aria-haspopup": "menu", "aria-expanded": isOpen, disabled: disabled, onClick: () => {
                        if (isOpen) {
                            close();
                            return;
                        }
                        open();
                        /*
                         * ...and move focus into the list, which is the whole point of this button:
                         * `Reactions.js` focuses the list whenever the popover opens with `autoFocusPopover`
                         * (its default is `true`). Opening without it left every item at `tabIndex={-1}` and
                         * focus on the trigger, so the menu was unreachable — and this button is only ever
                         * activated by keyboard, since it stays SR-only-clipped until `visibleTrigger`.
                         */
                        setActiveIdx(0);
                    } },
                    React.createElement(ChevronDownSmall, null))))),
        isOpen && (
        /*
         * `.withArrow` and `data-placement` are what CorePopover's placement rules key off; WUT
         * gets `data-placement` from Popper and passes moveArrowTo, so the arrow is shown.
         */
        React.createElement("div", { "data-part": "popover", "data-placement": "top-start", className: classNames(styles.popover, styles.withArrow) },
            React.createElement("div", { className: styles.arrow, style: { insetInlineStart: rtl ? 0 : 12 } }),
            React.createElement("div", { className: styles.popoverContent },
                React.createElement("div", { "data-hook": ReactionsDataHooks.PopoverContent, "data-part": "popover-content", onKeyDown: (e) => {
                        if (e.key === 'Tab') {
                            close();
                        }
                    } },
                    React.createElement("div", { role: "menu", "data-part": "list", className: styles.listRoot }, reactionDefinitions.map(({ id: reaction, label, icon }, index) => (React.createElement("div", { key: reaction, role: "menuitem", ref: (el) => itemRefs.current.set(index, el), 
                        /* roving tabindex: exactly one item is tabbable at a time */
                        tabIndex: index === (activeIdx ?? -1) ? 0 : -1, "data-part": "item", "data-id": reaction, "aria-label": label, "aria-current": selectedId === reaction, className: classNames(styles.item, {
                            [styles.active]: selectedId === reaction,
                        }), onMouseUp: (e) => {
                            e.preventDefault();
                            handleSelect(reaction);
                        }, onKeyDown: (e) => {
                            const last = reactionDefinitions.length - 1;
                            const current = activeIdx ?? 0;
                            switch (e.key) {
                                case 'Enter':
                                case ' ':
                                    e.preventDefault();
                                    handleSelect(reaction);
                                    break;
                                case 'ArrowLeft':
                                    e.preventDefault();
                                    setActiveIdx(current === 0 ? last : current - 1);
                                    break;
                                case 'ArrowRight':
                                    e.preventDefault();
                                    setActiveIdx(current === last ? 0 : current + 1);
                                    break;
                                case 'Home':
                                    e.preventDefault();
                                    setActiveIdx(0);
                                    break;
                                case 'End':
                                    e.preventDefault();
                                    setActiveIdx(last);
                                    break;
                                default:
                                    break;
                            }
                        } },
                        React.createElement("div", { className: styles.iconWrapper, "data-part": "icon" }, icon),
                        React.createElement("div", { className: styles.itemLabel, "aria-hidden": "true" }, label)))))))))));
}
//# sourceMappingURL=Reactions.js.map